import React, { useState, ReactNode } from 'react';
import { isEmpty } from 'lodash';

import { Popover, PopoverAnchor, PopoverContent, PopoverChevron } from '@fiverr-private/popover';
import { I18n } from '@fiverr-private/i18n-react';
import { Heading, Link } from '@fiverr-private/typography';
import { ComplianceBadge as Badge } from '@fiverr-private/badges';
import { Divider, Stack, Container } from '@fiverr-private/layout_components';
import {
    ListStatus,
    SellerLegalDocsStats,
    LegalDocumentsData,
    useTemplatesData,
} from '@fiverr-private/legal_documents_package';
import { Button } from '@fiverr-private/button';
import pathfinder from '@fiverr-private/pathfinder';
import {
    useComplianceContext,
    getBackgroundCheckStatus,
    getWorkforceClassificationStatus,
} from '../ComplianceProvider';
import { ComplianceStatusRank } from '../../types/complianceTypes';
import { useSellerConfigData } from '../../api/useSellerConfigData';
import { reportEventsApi } from '../../biEvents';

interface ComplianceItem {
    status?: ReactNode;
    label: string;
}

const badgeTypes: Record<
    Exclude<ComplianceStatusRank, ComplianceStatusRank.UNDEFINED>,
    'info' | 'success' | 'warning'
> = {
    [ComplianceStatusRank.BLUE]: 'info',
    [ComplianceStatusRank.GREEN]: 'success',
    [ComplianceStatusRank.YELLLOW]: 'warning',
};

export const ComplianceBadge = ({
    requeseteeId,
    sellerId,
    username,
    isCompact,
}: {
    requeseteeId?: number; // backward compabality
    sellerId?: number;
    username: string;
    isCompact?: boolean;
}) => {
    const context = useComplianceContext();
    const [isOpen, setIsOpen] = useState(false);

    const resolvedSellerId = sellerId ?? requeseteeId;

    const openModalOptions = {
        sellerId: resolvedSellerId as number,
        sellerName: username || '',
    };

    const {
        legalDocsConfigurations,
        isEligibleForBackgroundCheck,
        shouldShowBackgroundCheck,
        shouldShowLegalDocs,
        shouldShowWorkforceClassification,
    } = useSellerConfigData(resolvedSellerId || 0);

    if (!context || !resolvedSellerId) return null;

    const sellerComplianceState = context.getSellerComplianceState(resolvedSellerId);

    if (!sellerComplianceState.hasComplianceProcess) {
        return null;
    }

    const badgeType = badgeTypes[sellerComplianceState.compliaceRank];

    if (!badgeType) return null;

    return (
        <>
            <Popover triggerEvent="hover" onOpenChanged={setIsOpen} isOpen={isOpen} offset={8} position="bottom">
                <PopoverAnchor>
                    <Badge isCompact={isCompact} type={badgeType} />
                </PopoverAnchor>
                <PopoverContent appendToBody minWidth={296} disablePadding>
                    <PopoverChevron size="sm" />
                    <Stack paddingY={'4'} paddingX={'5'} gap={'4'} direction={'column'}>
                        <Heading size={'h_xxs'}>
                            <I18n k={`compliance.popover.title`} />
                        </Heading>
                        {shouldShowLegalDocs && (
                            <Stack direction={'column'} gap={'3'}>
                                <Stack alignItems={'center'} gap={'6'} justifyContent={'spaceBetween'}>
                                    <span>
                                        <I18n k={`compliance.types.legal_docs`} />
                                    </span>
                                    <LegalDocsItemStatus
                                        sellerId={resolvedSellerId}
                                        closePopover={() => setIsOpen(false)}
                                        legalDocsData={sellerComplianceState.legalDocsData}
                                        legalDocsInitialData={sellerComplianceState.legalDocsInitialData}
                                        isAllowedToUpload={!!legalDocsConfigurations?.allowedActions?.isAllowedToUpload}
                                        handleOpenModals={() => context?.openSendLegalDocsModal(openModalOptions)}
                                    />
                                </Stack>
                            </Stack>
                        )}
                        {isEligibleForBackgroundCheck && shouldShowBackgroundCheck && (
                            <Stack direction={'column'} gap={'3'}>
                                <Stack alignItems={'center'} gap={'6'} justifyContent={'spaceBetween'}>
                                    <span>
                                        <I18n k={`compliance.types.background_check`} />
                                    </span>
                                    <ComplianceItemStatus
                                        onClick={() => {
                                            setIsOpen(false);
                                            reportEventsApi.clickedOnBackgroundCheck({
                                                pageName: context.pageName,
                                                sellerId: resolvedSellerId,
                                                type: 'badge',
                                            });
                                            context?.openBackgroundCheckModal(openModalOptions);
                                        }}
                                        item={{
                                            label: 'background_check',
                                            status: getBackgroundCheckStatus(sellerComplianceState.backgroundCheckData),
                                        }}
                                    />
                                </Stack>
                            </Stack>
                        )}
                        {shouldShowWorkforceClassification && (
                            <Stack direction={'column'} gap={'3'}>
                                <Stack alignItems={'center'} gap={'6'} justifyContent={'spaceBetween'}>
                                    <span>
                                        <I18n k={`compliance.first_time_compliance_section.types.classification`} />
                                    </span>
                                    <ComplianceItemStatus
                                        onClick={() => {
                                            setIsOpen(false);
                                            reportEventsApi.clickedOnRequestAudit({
                                                pageName: context.pageName,
                                                sellerId: resolvedSellerId,
                                                type: 'badge',
                                            });
                                            context?.openWFCModal(openModalOptions);
                                        }}
                                        item={{
                                            label: 'workforce_classification',
                                            status: getWorkforceClassificationStatus(
                                                sellerComplianceState.classificationRequestData
                                            ),
                                        }}
                                    />
                                </Stack>
                            </Stack>
                        )}
                        <Divider />
                        <Stack justifyContent={'flexEnd'}>
                            <Button size="sm" intent="secondary" variant="outline">
                                <Link
                                    decoration={'none'}
                                    href={pathfinder('freelancer_page', { username })}
                                    target={'_blank'}
                                >
                                    <I18n k={`compliance.popover.action`} />
                                </Link>
                            </Button>
                        </Stack>
                    </Stack>
                </PopoverContent>
            </Popover>
        </>
    );
};

function ComplianceItemStatus(props: { item: ComplianceItem; onClick: any }) {
    const { item, onClick } = props;

    if (item.status) {
        return <Container>{item.status}</Container>;
    }

    return (
        <Link cursor="pointer" onClick={onClick}>
            <I18n k={`compliance.item_status.request`} />
        </Link>
    );
}

function LegalDocsItemStatus({
    legalDocsInitialData,
    legalDocsData,
    closePopover,
    isAllowedToUpload,
    handleOpenModals,
    sellerId,
}: {
    legalDocsInitialData: SellerLegalDocsStats | undefined;
    legalDocsData: LegalDocumentsData | undefined;
    closePopover: () => void;
    isAllowedToUpload: boolean;
    handleOpenModals: (activeTemplates: number, totalTemplates: number) => void;
    sellerId: number;
}) {
    const context = useComplianceContext();

    const totalTemplates = legalDocsData?.totalTemplates;
    const sellerDocsStatuses = legalDocsInitialData?.statuses;

    const { sellerDataQuery } = useTemplatesData({ sellerId });
    const selllerDocumentsData = sellerDataQuery?.data;
    if (!isEmpty(sellerDocsStatuses) || !isEmpty(selllerDocumentsData)) {
        return <ListStatus documents={selllerDocumentsData} initialData={legalDocsInitialData} hideDate />;
    }
    const triggerButtonCtaPath = totalTemplates ? 'send_docs' : 'setup';
    const redirectUrl = isAllowedToUpload && !totalTemplates ? pathfinder('legal_documents_page_render') : undefined;
    return (
        <Link
            cursor="pointer"
            href={redirectUrl}
            onClick={() => {
                if (context) {
                    if (totalTemplates) {
                        reportEventsApi.clickedOnSendDocument({ pageName: context.pageName });
                    } else {
                        reportEventsApi.clickOnLegalDocument({
                            pageName: context.pageName,
                            type: 'badge',
                        });
                    }
                }
                closePopover();
                if (!redirectUrl) {
                    handleOpenModals(legalDocsData?.activeTemplates || 0, totalTemplates || 0);
                }
            }}
            target="_blank"
            rel="noreferrer"
        >
            <I18n k={`compliance.item_status.${triggerButtonCtaPath}`} />
        </Link>
    );
}
